import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

import type { OngletConversations } from '../api/useConversations';
import type { EtatListe } from '../screens/messagerie/EtatListeContext';

interface UsePositionRestaureeParams {
    refCorps: RefObject<HTMLDivElement>;
    onglet: OngletConversations;
    /**
     * Le terme de recherche courant (`saisie`, pas le terme retenu après le
     * silence de 300 ms) : c'est ce que l'utilisateur voit dans le champ, et donc
     * ce qu'il s'attend à retrouver en revenant d'une conversation.
     */
    recherche: string;
    /**
     * La position mémorisée (`etat.defilement`), passée à chaque rendu. Seul son
     * premier rendu restauré compte (garde `restaure`) ; elle n'a pas à être figée
     * par l'appelant.
     */
    defilementInitial: number;
    enregistrer: (etat: EtatListe) => void;
    isLoading: boolean;
}

/**
 * Retrouver l'onglet et la position en revenant d'une conversation — audit
 * 2026-09-23 (B2). `Messagerie` reste monté entre `/messaging` et
 * `/messaging/:id` : cet état vit en mémoire, dans `EtatListeContext`.
 *
 * La position est SUIVIE en continu : lue au démontage, le nœud est déjà retiré
 * du DOM et `scrollTop` vaut 0.
 */
export function usePositionRestauree({
    refCorps,
    onglet,
    recherche,
    defilementInitial,
    enregistrer,
    isLoading,
}: UsePositionRestaureeParams) {
    const defilement = useRef(defilementInitial);
    const ongletCourant = useRef(onglet);
    ongletCourant.current = onglet;
    const rechercheCourante = useRef(recherche);
    rechercheCourante.current = recherche;

    useEffect(() => {
        const corps = refCorps.current;
        if (!corps) return;
        const suivre = () => {
            defilement.current = corps.scrollTop;
        };
        corps.addEventListener('scroll', suivre, { passive: true });
        return () => {
            corps.removeEventListener('scroll', suivre);
            enregistrer({
                onglet: ongletCourant.current,
                recherche: rechercheCourante.current,
                defilement: defilement.current,
            });
        };
    }, [enregistrer, refCorps]);

    const restaure = useRef(false);
    useEffect(() => {
        if (restaure.current || isLoading || !refCorps.current) return;
        restaure.current = true;
        refCorps.current.scrollTop = defilementInitial;
    }, [isLoading, defilementInitial, refCorps]);
}
